import React, { useState, useEffect, useRef } from 'react'
import { Typography, withStyles, Alert, Box } from '@material-ui/core'
import {
  CoreTextField,
  CoreButton,
  CoreAutoComplete,
} from 'src/components/core'
import { useDispatch, useSelector } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import {
  aICreatorUploadAttachmentSuccess,
  aICreatorSetCourseAttachments,
} from 'src/reducers/ai-creator/action'
import { getCourseAttachmentsService } from 'src/api/course'
import { LimitTypeEnum } from 'src/model/limit'
import UserType from 'src/model/user-types'
import { track } from '@amplitude/analytics-browser'
import moment from 'moment-timezone'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { userLimitsGetAction } from 'src/reducers/user-limits/action'
import { selectUserLimits } from 'src/reducers/user-limits/selector'

import styles from './styles'
import {
  ResourceTypeSelector,
  QuestionCountSelector,
  LoadingView,
  PdfView,
  PageGoals,
} from './components'

// Maps the internal resourceType to the label used in analytics events.
const sourceLabel = type => (type === 'course' ? 'course source' : type)

const AiQuizPopup = props => {
  const { sectionOptions, classes, onSubmit, mode = 'quiz' } = props
  const dispatch = useDispatch()
  const userLimits = useSelector(selectUserLimits)
  const cardCreationProgress = useSelector(
    state => state.course.cardCreationProgress,
  )
  const courseId = useSelector(state => state.course.course?.id)
  const existingAttachments = useSelector(
    state => state.aICreator.attachments,
  )

  // State
  const [description, setDescription] = useState('')
  const [pageGoals, setPageGoals] = useState([''])
  const [resourceType, setResourceType] = useState('course')
  const [courseAttachmentsLoading, setCourseAttachmentsLoading] = useState(
    mode === 'page',
  )
  const [hasCourseAttachments, setHasCourseAttachments] = useState(true)
  const [questionCount, setQuestionCount] = useState(1)
  const [selectedSection, setSelectedSection] = useState({
    name: 'All sections',
    value: 'allsections',
  })
  const [selectedTitle, setSelectedTitle] = useState(null)

  // Limit state
  const [aiCount, setAiCount] = useState(0)
  const [aiLimit, setAiLimit] = useState(0)
  const [refreshesAt, setRefreshesAt] = useState('')
  const [limitLoading, setLimitLoading] = useState(true)
  const [userType, setUserType] = useState('')

  // Scroll-down indicator (mode='page'): goals appear at the bottom of the
  // modal — show a chevron button when there's content below the viewport.
  const scrollContainerRef = useRef(null)
  const [canScrollDown, setCanScrollDown] = useState(false)

  // Calculate credit cost for quiz generation (1 credit per quiz generation)
  const creditCost = 1
  const remainingCredits = aiLimit - aiCount
  const hasEnoughCredits = remainingCredits >= creditCost

  // Track previous cardCreationProgress to only reset on completion (true→false)
  const prevCardCreationProgress = useRef(cardCreationProgress)

  // Reset form when cardCreationProgress transitions from true to false
  useEffect(() => {
    if (prevCardCreationProgress.current && !cardCreationProgress) {
      setDescription('')
      setPageGoals([''])
      setResourceType('course')
      setQuestionCount(1)
      setSelectedSection({
        name: 'All sections',
        value: 'allsections',
      })
      setSelectedTitle(null)
    }
    prevCardCreationProgress.current = cardCreationProgress
  }, [cardCreationProgress])

  const courseAttachmentsCache = useRef([])
  const flattenedHeadersCache = useRef([])

  const fetchCourseAttachments = () => {
    if (mode === 'page' && courseId) {
      setCourseAttachmentsLoading(true)
      getCourseAttachmentsService(courseId)
        .then(response => {
          if (response && response.length > 0) {
            courseAttachmentsCache.current = response
            dispatch(aICreatorSetCourseAttachments(response))
            setHasCourseAttachments(true)
          } else {
            setHasCourseAttachments(false)
            setResourceType('ai')
          }
        })
        .catch(() => {
          setHasCourseAttachments(false)
          setResourceType('ai')
        })
        .finally(() => {
          setCourseAttachmentsLoading(false)
        })
    } else {
      setCourseAttachmentsLoading(false)
    }
  }

  // Fetch course attachments on mount for page mode
  useEffect(() => {
    fetchCourseAttachments()
  }, [courseId, mode, dispatch])

  // Fetch user limits on component mount
  useEffect(() => {
    if (!userLimits) {
      dispatch(userLimitsGetAction())
    }
  }, [dispatch, userLimits])

  // Track whether the scroll container has more content below the viewport.
  // PageGoals sits at the very bottom in mode='page', so "not scrolled to
  // bottom" effectively means "goals aren't fully visible yet".
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const check = () => {
      const hasMore = el.scrollTop + el.clientHeight < el.scrollHeight - 4
      setCanScrollDown(hasMore)
    }
    check()
    el.addEventListener('scroll', check, { passive: true })
    let resizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(check)
      resizeObserver.observe(el)
      Array.from(el.children).forEach(child => resizeObserver.observe(child))
    }
    return () => {
      el.removeEventListener('scroll', check)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [
    resourceType,
    description,
    pageGoals,
    selectedTitle,
    selectedSection,
    existingAttachments.length,
    cardCreationProgress,
    limitLoading,
  ])

  const scrollToGoals = () => {
    const el = scrollContainerRef.current
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (userLimits) {
      setLimitLoading(true)
      const aiData = userLimits.find(
        x => x.limit.type === LimitTypeEnum.AICourseCreation,
      )

      if (aiData) {
        setAiCount(aiData.count)
        setAiLimit(aiData.limit.upperLimit)
        setRefreshesAt(aiData.refreshedAt)
        setUserType(userLimits[0].limit.roleType)
      }
      setLimitLoading(false)
    }
  }, [userLimits])

  const handleResourceTypeChange = newResourceType => {
    if (mode === 'page') {
      track('ai_page_resource_type_selected', {
        resource_type: sourceLabel(newResourceType),
      })
      // Switching to My resources: start with empty uploader
      // Switching back to Course source: restore cached attachments (no refetch)
      if (newResourceType === 'course') {
        dispatch(aICreatorSetCourseAttachments(courseAttachmentsCache.current))
      } else {
        dispatch(aICreatorSetCourseAttachments([]))
      }
    } else {
      dispatch(aICreatorUploadAttachmentSuccess(null, null))
    }
    setResourceType(newResourceType)
    setSelectedTitle(null)
    setDescription('')
    setPageGoals([''])
    if (newResourceType === 'ai') {
      const inputElement = document.getElementById('description-input')
      if (inputElement) {
        inputElement.focus()
      }
    }
  }

  const handleSectionChange = (event, value) => {
    setSelectedSection(value)
  }

  const handlePdfChange = data => {
    setSelectedTitle(data.selectedContent)
  }

  const handleSubmit = () => {
    if (resourceType === 'ai' && !description.trim()) {
      return
    }

    // Check if user has enough AI credits
    if (!hasEnoughCredits) {
      return
    }

    const trimmedGoals =
      mode === 'page'
        ? pageGoals.map(g => g.trim()).filter(g => g !== '')
        : []

    const quizData = {
      description: resourceType === 'ai' && description.trim(),
      resourceType,
      questionCount,
      pageGoals: trimmedGoals,
    }

    if (resourceType === 'course' && mode === 'page' && existingAttachments.length > 0) {
      if (!selectedTitle) {
        return
      }
      quizData.resourceType = 'pdf'
      quizData.selectedContent = selectedTitle
      quizData.attachments = selectedTitle
    } else if (resourceType === 'course') {
      quizData.section = selectedSection
    } else if (resourceType === 'pdf') {
      if (!selectedTitle) {
        return
      }
      quizData.selectedContent = selectedTitle
      quizData.attachments = selectedTitle
    }

    // Preserve the UI source selection (course / ai / pdf) and the chosen
    // document name for analytics, independent of the backend resourceType
    // remap above.
    quizData.selectedSource = sourceLabel(resourceType)
    quizData.documentName = selectedTitle?.fileName || null
    quizData.sectionName =
      resourceType === 'pdf'
        ? selectedTitle?.displayTitle || null
        : resourceType === 'course'
        ? selectedTitle?.displayTitle || selectedSection?.name || null
        : null

    if (mode === 'page') {
      const eventProps = { resource_type: sourceLabel(resourceType) }
      if (resourceType === 'ai') {
        eventProps.prompt = quizData.description
      } else {
        eventProps.section = quizData.sectionName
        eventProps.document_name = quizData.documentName
      }
      track('started_generating_ai_page', eventProps)
    }

    if (onSubmit) {
      onSubmit(quizData)
    }
  }

  const handleUpgrade = () => {
    track('limit_warning_upgrade', {
      from: 'ai_quiz_popup_upgrade',
    })
  }

  if (cardCreationProgress) {
    return <LoadingView mode={mode} />
  }

  if (limitLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  return (
    <div>
      <div className={classes.scrollWrapper}>
        <div ref={scrollContainerRef} className={classes.rootContainer}>
        {/* Show warning if user doesn't have enough credits */}
        {!hasEnoughCredits ? (
          <Alert severity="warning" className={classes.limitWarning}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">
                You don't have enough AI credits to generate{' '}
                {mode === 'page' ? 'pages' : 'a quiz'}.
                {refreshesAt && (
                  <span>
                    {' '}
                    Your credits will be renewed on{' '}
                    {moment(refreshesAt).add('days', 30).format('L')}.
                  </span>
                )}
              </Typography>
            </Box>
          </Alert>
        ) : (
          <>
            <ResourceTypeSelector
              resourceType={resourceType}
              onResourceTypeChange={handleResourceTypeChange}
              mode={mode}
              disableCourse={mode === 'page' && !hasCourseAttachments}
            />

            {resourceType === 'ai' && (
              <div>
                <CoreTextField
                  id="description-input"
                  autoFocus
                  multiline
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  variant="outlined"
                  fullWidth
                  label={mode === 'page' ? 'Page topic' : 'Description'}
                  placeholder={
                    mode === 'page'
                      ? 'Describe the topic or concept of the page.'
                      : 'Describe the topic or concept you want to generate questions from.'
                  }
                  marginTop={true}
                />
                {mode === 'page' && description.trim() && (
                  <PageGoals
                    topic={description}
                    selectionKey={description.trim()}
                    goals={pageGoals}
                    onGoalsChange={setPageGoals}
                  />
                )}
              </div>
            )}

            {resourceType === 'course' &&
              (mode === 'page' && existingAttachments.length > 0 ? (
                <>
                  <PdfView
                    onChange={handlePdfChange}
                    isPage
                    initialFlattenedHeaders={flattenedHeadersCache.current}
                    onFlattenedHeadersChange={headers => {
                      flattenedHeadersCache.current = headers
                    }}
                  />
                  {selectedTitle && (
                    <PageGoals
                      topic={selectedTitle?.element_id || ''}
                      attachmentIds={existingAttachments
                        .map(a => a.attachment_id)
                        .filter(Boolean)}
                      selectionKey={selectedTitle?.element_id || ''}
                      goals={pageGoals}
                      onGoalsChange={setPageGoals}
                    />
                  )}
                </>
              ) : (
                <>
                  <CoreAutoComplete
                    marginTop={true}
                    fullWidth
                    label={
                      mode === 'page'
                        ? 'Select section(s) AI will extract content from'
                        : 'Select section AI will extract questions from'
                    }
                    defaultValue={{ name: 'All sections', value: 'allsections' }}
                    value={selectedSection}
                    selectOnFocus
                    options={sectionOptions}
                    getOptionLabel={option => option.name || ''}
                    getOptionSelected={(option, value) =>
                      option.value === value.value
                    }
                    onChange={handleSectionChange}
                    freeSolo={false}
                  />
                  {mode === 'page' && selectedSection?.value && (
                    <PageGoals
                      topic={selectedSection?.name || ''}
                      selectionKey={selectedSection?.value || ''}
                      goals={pageGoals}
                      onGoalsChange={setPageGoals}
                    />
                  )}
                </>
              ))}

            {resourceType === 'pdf' && (
              <>
                <PdfView onChange={handlePdfChange} />
                {mode === 'page' && selectedTitle && (
                  <PageGoals
                    topic={selectedTitle?.element_id || ''}
                    attachmentIds={existingAttachments
                      .map(a => a.attachment_id)
                      .filter(Boolean)}
                    selectionKey={selectedTitle?.element_id || ''}
                    goals={pageGoals}
                    onGoalsChange={setPageGoals}
                  />
                )}
              </>
            )}

            {mode !== 'page' && (
              <QuestionCountSelector
                resourceType={resourceType}
                questionCount={questionCount}
                onQuestionCountChange={setQuestionCount}
                mode={mode}
              />
            )}
          </>
        )}
        </div>
        {mode === 'page' && hasEnoughCredits && canScrollDown && (
          <div
            className={classes.scrollDownArrow}
            onClick={scrollToGoals}
            role="button"
            tabIndex={0}
            aria-label="Scroll down to page goals"
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') scrollToGoals()
            }}
          >
            <Icon icon="line-md:chevron-down" fontSize={20} />
          </div>
        )}
      </div>
      <div className={classes.buttonContainer}>
        <CoreButton
          variant="text"
          color="secondary"
          onClick={() => dispatch(messageBoxHideAction('message-box-1'))}
        >
          Back
        </CoreButton>
        {hasEnoughCredits ? (
          <CoreButton
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleSubmit}
            disabled={
              (resourceType === 'ai' && !description.trim()) ||
              (resourceType === 'pdf' && !selectedTitle) ||
              (resourceType === 'course' && mode === 'page' && existingAttachments.length > 0 && !selectedTitle) ||
              (resourceType === 'course' && !(mode === 'page' && existingAttachments.length > 0) && !selectedSection) ||
              (mode === 'page' && pageGoals.filter(g => g && g.trim()).length === 0)
            }
          >
            {mode === 'page' ? 'Generate' : 'Generate Questions'}{' '}
            <Icon icon="mage:coin-a" style={{ margin: '0 4px' }} />{' '}
            {questionCount}
          </CoreButton>
        ) : (
          <CoreButton
            variant="contained"
            color="primary"
            size="small"
            to="/pricing"
            component={RouterLink}
            onClick={handleUpgrade}
            startIcon={
              <Icon
                size="small"
                icon="fa6-solid:crown"
                color="#f7b155"
                width={15}
              />
            }
          >
            Upgrade Plan
          </CoreButton>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(AiQuizPopup)
